import { useState, useCallback, useEffect } from 'react';
import { Select, Input, AutoComplete, Spin, Button, Modal, Table, Tag, message } from 'antd';
import { FileTextOutlined, EnvironmentOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { buildDropdownStateProps } from '../../common/dropdownLoaderState';
import { loadCustomersSafe, loadCustomerByIdSafe } from './proformaApi';
import { useTypeaheadSearch } from './useTypeaheadSearch';
import { placeOfSupplyForCustomer } from './proformaHelpers';
import {
  filterPincodeInput, filterMobileInput,
  pincodeFormatError, mobileFormatError, gstinFormatError, validateParty
} from './proformaCustomerValidation';

// ── Stage 6Q.1 — Bill To / Ship To (GlassMaster HTML look) ──
// Two sibling flex columns inside the editor's header strip, matched to the
// reference render: underline inputs (no boxes), label + field rows, City+State
// / Ph+Mob / GSTIN+State Code pairs, and a Place-of-Supply row with button on
// Ship To. Master binding is preserved invisibly: the Bill To "Name" field is a
// Customer-Master AutoComplete — typing searches the master and selecting
// autofills the full Bill To / Ship To snapshot. Customer / state data is
// fetched live from the masters, never hardcoded.

// Scoped underline styling so AntD inputs/selects read as the reference's
// border-bottom fields rather than boxed controls.
const CUST_CSS = `
.pf-cust .ant-input{border:0;border-bottom:1px solid #cbd5e1;border-radius:0;padding:1px 2px;font-size:13px;background:transparent;color:#1e293b;transition:border-color .15s ease;}
.pf-cust .ant-input::placeholder{color:#cbd5e1;font-weight:400;}
.pf-cust .ant-input:hover{border-bottom-color:#94a3b8;}
.pf-cust .ant-input:focus,.pf-cust .ant-input-focused{box-shadow:none;border-bottom-color:#1877F2;}
.pf-cust .ant-input[disabled]{background:transparent;color:#475569;border-bottom-style:dotted;}
.pf-cust .ant-select .ant-select-selector{border:0!important;border-bottom:1px solid #cbd5e1!important;border-radius:0!important;box-shadow:none!important;padding:0 2px!important;background:transparent!important;transition:border-color .15s ease;}
.pf-cust .ant-select:hover .ant-select-selector{border-bottom-color:#94a3b8!important;}
.pf-cust .ant-select-focused .ant-select-selector{border-bottom-color:#1877F2!important;}
.pf-cust .ant-select .ant-select-selection-item{color:#1e293b;}
.pf-cust .ant-select-selection-placeholder{font-size:12px;color:#cbd5e1;}
/* Name AutoComplete wraps an Input — kill the select's own border/box so only
   the inner input underline shows, and match the small input height (24px). */
.pf-name.ant-select .ant-select-selector,
.pf-name.ant-select-focused .ant-select-selector{border:0!important;box-shadow:none!important;}
.pf-name.ant-select.ant-select-sm .ant-select-selector{height:24px!important;padding:0!important;}
/* SC / POS code chips */
.pf-cust .pf-code.ant-input{border:1px solid #cbd5e1;border-radius:5px;background:#f1f5f9;color:#1e293b;font-weight:400;text-align:center;}
.pf-cust .pf-code.ant-input:hover{border-color:#94a3b8;}
.pf-cust .pf-code.ant-input:focus{border-color:#1877F2;background:#fff;}
/* Delivery-address picker modal — ultra-compact, professional */
.pf-delv-modal .ant-modal-content{padding:14px 16px;}
.pf-delv-modal .ant-modal-header{margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #eef2f6;}
.pf-delv-modal .ant-modal-title{font-size:14px;font-weight:700;color:#1e293b;}
.pf-delv-modal .ant-modal-close{top:12px;inset-inline-end:12px;width:22px;height:22px;}
.pf-delv-modal .ant-table-thead>tr>th{font-size:10px;font-weight:600;padding:4px 8px!important;background:#f8fafc;color:#64748b;text-transform:uppercase;letter-spacing:.03em;border-bottom:1px solid #e2e8f0;}
.pf-delv-modal .ant-table-tbody>tr>td{font-size:12px;padding:4px 8px!important;line-height:1.3;}
.pf-delv-modal .ant-table-tbody>tr:hover>td{background:#eff6ff!important;}
.pf-delv-modal .ant-table-cell{vertical-align:middle;}
.pf-delv-modal .ant-empty-normal{margin-block:14px;}
.pf-delv-modal .ant-pagination{margin-block:8px 0!important;}
.pf-delv-modal .ant-pagination .ant-pagination-item,.pf-delv-modal .ant-pagination-prev,.pf-delv-modal .ant-pagination-next{min-width:22px;height:22px;line-height:20px;}
`;

const LBL = 'text-[12px] text-slate-600 shrink-0';
const ROW = 'flex items-center gap-2 mb-1';
// Required-field marker (red asterisk).
const REQ = <span className="text-red-500"> *</span>;

function UField({ label, labelW = 52, value, onChange, readOnly, placeholder, required, status }) {
  return (
    <div className={ROW}>
      <span className={LBL} style={{ width: labelW }}>{label}{required ? REQ : null}</span>
      <Input size="small" status={status} value={value || ''} disabled={readOnly} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// Saved delivery (shipping) addresses come from the Customer Master detail
// (`addresses`) — drop soft-deleted / inactive rows.
function extractAddresses(customer) {
  const list = customer && Array.isArray(customer.addresses) ? customer.addresses : [];
  return list.filter((a) => a && !a.deleted && a.isActive !== false);
}
function deliveryLabel(a) {
  const parts = [a.tradeName, a.addressLine1, a.city].filter(Boolean);
  const base = parts.length ? parts.join(' · ') : 'Address';
  return a.isDefault ? `★ ${base}` : base;
}

export default function ProformaCustomerSection({ header, setHeader, dropdowns, dropdownsLoading, dropdownsError, readOnly }) {
  const [loadingCust, setLoadingCust] = useState(false);
  // Customer's saved shipping addresses + which delivery option is selected.
  // Picked via a searchable modal (scales to 100s of saved addresses).
  const [custAddresses, setCustAddresses] = useState([]);
  const [addrLoadedFor, setAddrLoadedFor] = useState(null);
  const [deliverySel, setDeliverySel] = useState(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addrSearch, setAddrSearch] = useState('');
  // Add-new-delivery-address form (ad-hoc for THIS proforma; applied as Ship To).
  const [addMode, setAddMode] = useState(false);
  const [newAddr, setNewAddr] = useState({});
  const [customLabel, setCustomLabel] = useState(null);
  const setNA = (patch) => setNewAddr((a) => ({ ...a, ...patch }));

  const bt = header.billTo || {};
  const st = header.shipTo || {};
  const setBill = (patch) => setHeader((h) => ({ ...h, billTo: { ...(h.billTo || {}), ...patch } }));
  const setShip = (patch) => setHeader((h) => ({ ...h, shipTo: { ...(h.shipTo || {}), ...patch } }));
  const stateOpts = (dropdowns.states || []).map((s) => ({ label: s.label, value: s.code }));
  const ddState = { loading: dropdownsLoading, error: dropdownsError, data: dropdowns.states };

  // Customer Master search — debounced + race-safe (5000+ customers, server-side).
  const fetchCustomers = useCallback(async (q) => {
    const { rows } = await loadCustomersSafe(q);
    return rows.map((c) => {
      const name = c.customerName || c.displayName || '(unnamed)';
      // Many distinct customers can share a trade name (and have no GSTIN),
      // so disambiguate the row with GSTIN, else customer code / city, and
      // key on the unique id (the name alone collides on React keys).
      const tail = c.gstin
        ? c.gstin
        : [c.customerCode, c.billingCity].filter(Boolean).join(' · ');
      return { key: c.id, id: c.id, value: name, label: tail ? `${name} · ${tail}` : name };
    });
  }, []);
  const { options, run: search, cancel: cancelSearch, searching } = useTypeaheadSearch(fetchCustomers, { delay: 250, minChars: 1 });

  const pickCustomer = useCallback(async (id) => {
    if (!id) return;
    setLoadingCust(true);
    const { customer } = await loadCustomerByIdSafe(id);
    setLoadingCust(false);
    setDeliverySel(undefined);
    if (!customer) {
      setCustAddresses([]); setAddrLoadedFor(id);
      setHeader((h) => ({ ...h, customerId: id }));
      return;
    }
    const addrs = extractAddresses(customer);
    setCustAddresses(addrs);
    setAddrLoadedFor(id);
    // Many customer records keep their address only in the address book (the
    // companion `addresses` table) and leave the master row's billing_* /
    // shipping_* columns blank. Fall back to the default saved address (or the
    // first one) for the address fields so Bill To / Ship To still populate.
    // The master columns always win when they are filled, so this never changes
    // behaviour for fully-entered customers.
    const defAddr = addrs.find((a) => a.isDefault) || addrs[0] || {};
    const billTo = {
      name: customer.customerName || customer.displayName || null,
      addressLine1: customer.billingAddressLine1 || defAddr.addressLine1 || null,
      // Add 2 was previously never mapped — Bill To always lost address line 2.
      addressLine2: customer.billingAddressLine2 || defAddr.addressLine2 || null,
      city: customer.billingCity || defAddr.city || null,
      pincode: customer.billingPincode || customer.pincode || defAddr.pincode || null,
      state: customer.billingState || customer.gstStateName || defAddr.state || null,
      stateCode: customer.billingStateCode || customer.gstStateCode || defAddr.stateCode || null,
      phone: customer.phone || null,
      mobile: customer.mobile || null,
      email: customer.email || null,
      gstin: customer.gstin || defAddr.gstin || null,
      placeOfSupplyStateCode: customer.placeOfSupplyStateCode || customer.gstStateCode || customer.billingStateCode || null
    };
    const shipTo = customer.sameAsBilling ? { ...billTo } : {
      name: customer.customerName || null,
      addressLine1: customer.shippingAddressLine1 || defAddr.addressLine1 || customer.billingAddressLine1 || null,
      addressLine2: customer.shippingAddressLine2 || defAddr.addressLine2 || customer.billingAddressLine2 || null,
      city: customer.shippingCity || defAddr.city || customer.billingCity || null,
      pincode: customer.shippingPincode || defAddr.pincode || customer.billingPincode || customer.pincode || null,
      state: customer.shippingState || defAddr.state || customer.billingState || null,
      stateCode: customer.shippingStateCode || defAddr.stateCode || customer.billingStateCode || null,
      phone: customer.phone || null,
      mobile: customer.mobile || null,
      email: customer.email || null,
      gstin: customer.gstin || defAddr.gstin || null
    };
    setHeader((h) => ({
      ...h, customerId: id, billTo, shipTo,
      // Fix 1 (SALES-PROFORMA-AUDIT-FIX-1) — Place of Supply follows the newly
      // picked customer. Do NOT OR-preserve the prior value: switching customer
      // A → B must adopt B's state so the GST supply type recomputes correctly.
      placeOfSupplyStateCode: placeOfSupplyForCustomer(billTo),
      isSez: customer.isSez === true
    }));
  }, [setHeader]);

  // When an existing proforma is opened (customerId already set, no pick),
  // lazily fetch that customer's saved shipping addresses so the delivery
  // picker is populated. Skips re-fetch when we already loaded them on pick.
  useEffect(() => {
    const cid = header.customerId;
    if (!cid) { setCustAddresses([]); setAddrLoadedFor(null); setDeliverySel(undefined); return; }
    if (addrLoadedFor === cid) return;
    let cancelled = false;
    loadCustomerByIdSafe(cid).then(({ customer }) => {
      if (cancelled) return;
      setCustAddresses(extractAddresses(customer));
      setAddrLoadedFor(cid);
    });
    return () => { cancelled = true; };
  }, [header.customerId, addrLoadedFor]);

  // Pick a delivery address: copy Bill To, or snapshot a saved shipping address.
  const onPickDelivery = useCallback((v) => {
    setDeliverySel(v);
    if (v === '__SAME__') {
      setHeader((h) => ({ ...h, shipTo: { ...(h.billTo || {}) } }));
      return;
    }
    const a = custAddresses.find((x) => String(x.id) === String(v));
    if (!a) return;
    setHeader((h) => ({
      ...h,
      shipTo: {
        name: a.tradeName || (h.billTo && h.billTo.name) || null,
        addressLine1: a.addressLine1 || null,
        addressLine2: a.addressLine2 || null,
        city: a.city || null,
        pincode: a.pincode || null,
        state: a.state || null,
        stateCode: a.stateCode || null,
        gstin: a.gstin || null,
        mobile: (h.billTo && h.billTo.mobile) || null,
        email: (h.billTo && h.billTo.email) || null
      }
    }));
  }, [custAddresses, setHeader]);

  // Pick from the modal, then close it.
  const chooseDelivery = useCallback((v) => { onPickDelivery(v); setPickerOpen(false); }, [onPickDelivery]);

  // Open the picker (reset the add-new form each time).
  const openPicker = useCallback(() => {
    setAddrSearch(''); setAddMode(false); setNewAddr({}); setPickerOpen(true);
  }, []);

  // Save a NEW (ad-hoc) delivery address as this proforma's Ship To. Enforces the
  // same mandatory validations as the Ship To party (Name, Addr 1, City, 6-digit
  // Pincode, Indian Mobile, GSTIN/PAN/Aadhaar/URP). Not persisted to the master.
  const saveNewDelivery = useCallback(() => {
    const errs = validateParty(newAddr, 'Delivery address');
    if (errs.length) { message.error(errs[0]); return; }
    const addr = {
      name: newAddr.name, addressLine1: newAddr.addressLine1, addressLine2: newAddr.addressLine2 || null,
      city: newAddr.city, pincode: newAddr.pincode, state: newAddr.state || null,
      stateCode: newAddr.stateCode || null, mobile: newAddr.mobile, email: newAddr.email || null,
      gstin: newAddr.gstin
    };
    setHeader((h) => ({ ...h, shipTo: addr }));
    setCustomLabel(deliveryLabel({ tradeName: addr.name, addressLine1: addr.addressLine1, city: addr.city }));
    setDeliverySel('__CUSTOM__');
    setAddMode(false); setNewAddr({}); setPickerOpen(false);
  }, [newAddr, setHeader]);

  // Client-side filter across the saved addresses — scales to 100s of rows
  // with the modal's own pagination doing the rest.
  const q = addrSearch.trim().toLowerCase();
  const filteredAddresses = !q ? custAddresses : custAddresses.filter((a) =>
    [a.tradeName, a.addressLine1, a.addressLine2, a.city, a.pincode, a.gstin, a.stateCode, a.state]
      .filter(Boolean).some((f) => String(f).toLowerCase().includes(q)));
  const selectedAddr = custAddresses.find((x) => String(x.id) === String(deliverySel));
  const currentDeliveryLabel = deliverySel === '__SAME__'
    ? 'Same as Bill To'
    : deliverySel === '__CUSTOM__'
      ? (customLabel || 'New delivery address')
      : (selectedAddr ? deliveryLabel(selectedAddr) : null);
  const addrColumns = [
    { title: '', dataIndex: 'isDefault', width: 28, align: 'center', render: (v) => v ? <span style={{ color: '#f59e0b' }}>★</span> : null },
    { title: 'Trade Name', dataIndex: 'tradeName', width: 140, ellipsis: true, render: (v) => v || '—' },
    { title: 'Address', ellipsis: true, render: (_v, r) => [r.addressLine1, r.addressLine2].filter(Boolean).join(', ') || '—' },
    { title: 'City', dataIndex: 'city', width: 100, ellipsis: true, render: (v) => v || '—' },
    { title: 'Pin', dataIndex: 'pincode', width: 64, render: (v) => v || '—' },
    { title: 'SC', dataIndex: 'stateCode', width: 42, render: (v) => v || '—' },
    { title: 'GSTIN', dataIndex: 'gstin', width: 132, ellipsis: true, render: (v) => v || '—' }
  ];

  return (
    <>
      <style>{CUST_CSS}</style>

      {/* ── Bill To ── */}
      <div className="pf-cust flex-1 min-w-[280px] px-3 py-2 border-r border-slate-200">
        <div className="flex items-center gap-2 mb-2.5 pb-1.5 border-b border-slate-200">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 text-[#1877F2]">
            <FileTextOutlined style={{ fontSize: 13 }} />
          </span>
          <span className="text-[14px] font-bold text-slate-800 tracking-tight">Bill To</span>
        </div>
        <Spin spinning={loadingCust} size="small">
          <div className={ROW}>
            <span className={LBL} style={{ width: 52 }}>Name{REQ}</span>
            <AutoComplete size="small" className="w-full pf-name" value={bt.name || ''} disabled={readOnly}
              options={options} filterOption={false} notFoundContent={searching ? <Spin size="small" /> : null}
              onSearch={search} onFocus={() => search(bt.name || '')}
              onBlur={() => cancelSearch()}
              onSelect={(_v, opt) => pickCustomer(opt && opt.id)}
              onChange={(v) => setBill({ name: v })}>
              <Input size="small" placeholder="Name / search Customer Master…" />
            </AutoComplete>
          </div>
          <UField label="Add" value={bt.addressLine1} onChange={(v) => setBill({ addressLine1: v })} readOnly={readOnly} required />
          <UField label="Add 2" value={bt.addressLine2} onChange={(v) => setBill({ addressLine2: v })} readOnly={readOnly} />
          <div className="flex gap-3">
            <div className="flex-1"><UField label="City" value={bt.city} onChange={(v) => setBill({ city: v })} readOnly={readOnly} required /></div>
            <div className="flex items-center gap-2 mb-1">
              <span className={LBL}>Pincode{REQ}</span>
              <Input size="small" style={{ width: 84 }} maxLength={6} status={pincodeFormatError(bt.pincode) ? 'error' : undefined}
                value={bt.pincode || ''} disabled={readOnly}
                onChange={(e) => setBill({ pincode: filterPincodeInput(e.target.value) })} />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex items-center gap-2 mb-1 flex-1">
              <span className={LBL} style={{ width: 52 }}>State</span>
              <Select size="small" className="flex-1" showSearch allowClear optionFilterProp="label"
                value={bt.stateCode || undefined} disabled={readOnly} {...buildDropdownStateProps(ddState)}
                options={stateOpts} onChange={(v, opt) => setBill({ stateCode: v || null, state: (opt && opt.label) || null })} placeholder="—" />
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className={LBL} style={{ width: 52, whiteSpace: 'nowrap' }}>Mobile{REQ}</span>
              <Input size="small" style={{ width: 92 }} status={mobileFormatError(bt.mobile) ? 'error' : undefined}
                value={bt.mobile || ''} disabled={readOnly} placeholder="98xxxxxxxx, …"
                onChange={(e) => setBill({ mobile: filterMobileInput(e.target.value) })} />
            </div>
          </div>
          <UField label="Email" value={bt.email} onChange={(v) => setBill({ email: v })} readOnly={readOnly} />
          {/* GSTIN + SC / POS (compact 2-digit codes; POS drives GST split) */}
          <div className="flex items-center gap-2 mb-1">
            <span className={LBL} style={{ width: 52 }}>GSTIN{REQ}</span>
            <Input size="small" className="flex-1 min-w-[56px]" status={gstinFormatError(bt.gstin) ? 'error' : undefined}
              value={bt.gstin || ''} disabled={readOnly} placeholder="GSTIN / PAN / Aadhaar / URP"
              onChange={(e) => setBill({ gstin: e.target.value })} />
            <div className="flex items-center gap-1 shrink-0">
              <span className={LBL}>SC</span>
              <Input size="small" style={{ width: 40 }} className="pf-code" maxLength={2}
                value={bt.stateCode || ''} disabled={readOnly} onChange={(e) => setBill({ stateCode: e.target.value })} />
              <span className="text-[13px] text-slate-400 px-0.5">/</span>
              <span className={LBL}>POS</span>
              <Input size="small" style={{ width: 40 }} className="pf-code" maxLength={2}
                value={header.placeOfSupplyStateCode || ''} disabled={readOnly}
                onChange={(e) => setHeader((h) => ({ ...h, placeOfSupplyStateCode: e.target.value || null }))} />
            </div>
          </div>
        </Spin>
      </div>

      {/* ── Ship To ── */}
      <div className="pf-cust flex-1 min-w-[280px] px-3 py-2">
        <div className="flex items-center gap-2 mb-2.5 pb-1.5 border-b border-slate-200">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 text-[#1877F2]">
            <EnvironmentOutlined style={{ fontSize: 13 }} />
          </span>
          <span className="text-[14px] font-bold text-slate-800 tracking-tight">Ship To</span>
          {!readOnly && (
            <Button size="small" className="ml-auto" icon={<EnvironmentOutlined />}
              onClick={openPicker} style={{ maxWidth: 230 }}>
              <span className="truncate" style={{ maxWidth: 178, display: 'inline-block', verticalAlign: 'bottom' }}>
                {currentDeliveryLabel || 'Choose delivery address'}
              </span>
            </Button>
          )}
        </div>
        <UField label="Name" value={st.name} onChange={(v) => setShip({ name: v })} readOnly={readOnly} required />
        <UField label="Add" value={st.addressLine1} onChange={(v) => setShip({ addressLine1: v })} readOnly={readOnly} required />
        <UField label="Add 2" value={st.addressLine2} onChange={(v) => setShip({ addressLine2: v })} readOnly={readOnly} />
        <div className="flex gap-3">
          <div className="flex-1"><UField label="City" value={st.city} onChange={(v) => setShip({ city: v })} readOnly={readOnly} required /></div>
          <div className="flex items-center gap-2 mb-1">
            <span className={LBL}>Pincode{REQ}</span>
            <Input size="small" style={{ width: 84 }} maxLength={6} status={pincodeFormatError(st.pincode) ? 'error' : undefined}
              value={st.pincode || ''} disabled={readOnly}
              onChange={(e) => setShip({ pincode: filterPincodeInput(e.target.value) })} />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 mb-1 flex-1">
            <span className={LBL} style={{ width: 52 }}>State</span>
            <Select size="small" className="flex-1" showSearch allowClear optionFilterProp="label"
              value={st.stateCode || undefined} disabled={readOnly} {...buildDropdownStateProps(ddState)}
              options={stateOpts} onChange={(v, opt) => setShip({ stateCode: v || null, state: (opt && opt.label) || null })} placeholder="—" />
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className={LBL} style={{ width: 52, whiteSpace: 'nowrap' }}>Mobile{REQ}</span>
            <Input size="small" style={{ width: 92 }} status={mobileFormatError(st.mobile) ? 'error' : undefined}
              value={st.mobile || ''} disabled={readOnly} placeholder="98xxxxxxxx, …"
              onChange={(e) => setShip({ mobile: filterMobileInput(e.target.value) })} />
          </div>
        </div>
        <UField label="Email" value={st.email} onChange={(v) => setShip({ email: v })} readOnly={readOnly} />
        {/* GSTIN + SC (aligns with Bill To's GSTIN row) */}
        <div className="flex items-center gap-2 mb-1">
          <span className={LBL} style={{ width: 52 }}>GSTIN{REQ}</span>
          <Input size="small" className="flex-1 min-w-[56px]" status={gstinFormatError(st.gstin) ? 'error' : undefined}
            value={st.gstin || ''} disabled={readOnly} placeholder="GSTIN / PAN / Aadhaar / URP"
            onChange={(e) => setShip({ gstin: e.target.value })} />
          <div className="flex items-center gap-1 shrink-0">
            <span className={LBL}>SC</span>
            <Input size="small" style={{ width: 40 }} className="pf-code" maxLength={2}
              value={st.stateCode || ''} disabled={readOnly} onChange={(e) => setShip({ stateCode: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Delivery-address picker — searchable modal (scales to 100s of rows) */}
      <Modal open={pickerOpen} onCancel={() => setPickerOpen(false)} footer={null}
        className="pf-delv-modal" title="Select delivery address" width={860} destroyOnClose>
        <div className="flex items-center gap-2 mb-2">
          <Button size="small" icon={<FileTextOutlined />} disabled={addMode} onClick={() => chooseDelivery('__SAME__')}>Same as Bill To</Button>
          <Button size="small" type={addMode ? 'primary' : 'default'} icon={<PlusOutlined />} onClick={() => setAddMode(true)}>Add New Address</Button>
          {!addMode && (
            <Input size="small" allowClear prefix={<SearchOutlined style={{ color: '#94a3b8' }} />} className="flex-1"
              placeholder="Search name, address, city, pincode, GSTIN…"
              value={addrSearch} onChange={(e) => setAddrSearch(e.target.value)} />
          )}
        </div>

        {addMode ? (
          /* Add a NEW delivery address (ad-hoc → applied as Ship To). All
             mandatory fields validated the same way as the Ship To party. */
          <div className="pf-cust" style={{ maxWidth: 440 }}>
            {/* Same field order as Ship To: Name, Add, Add 2, City|Pincode,
                State|Mobile, Email, GSTIN. */}
            <UField label="Name" labelW={64} value={newAddr.name} onChange={(v) => setNA({ name: v })} required />
            <UField label="Add" labelW={64} value={newAddr.addressLine1} onChange={(v) => setNA({ addressLine1: v })} required />
            <UField label="Add 2" labelW={64} value={newAddr.addressLine2} onChange={(v) => setNA({ addressLine2: v })} />
            <div className="flex gap-3">
              <div className="flex-1"><UField label="City" labelW={64} value={newAddr.city} onChange={(v) => setNA({ city: v })} required /></div>
              <div className="flex items-center gap-2 mb-1">
                <span className={LBL} style={{ width: 56, whiteSpace: 'nowrap' }}>Pincode{REQ}</span>
                <Input size="small" style={{ width: 84 }} maxLength={6} status={pincodeFormatError(newAddr.pincode) ? 'error' : undefined}
                  value={newAddr.pincode || ''} onChange={(e) => setNA({ pincode: filterPincodeInput(e.target.value) })} />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex items-center gap-2 mb-1 flex-1">
                <span className={LBL} style={{ width: 64 }}>State</span>
                <Select size="small" className="flex-1" showSearch allowClear optionFilterProp="label"
                  value={newAddr.stateCode || undefined} {...buildDropdownStateProps(ddState)} options={stateOpts}
                  onChange={(v, opt) => setNA({ stateCode: v || null, state: (opt && opt.label) || null })} placeholder="—" />
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className={LBL} style={{ width: 52, whiteSpace: 'nowrap' }}>Mobile{REQ}</span>
                <Input size="small" style={{ width: 92 }} status={mobileFormatError(newAddr.mobile) ? 'error' : undefined} placeholder="98xxxxxxxx, …"
                  value={newAddr.mobile || ''} onChange={(e) => setNA({ mobile: filterMobileInput(e.target.value) })} />
              </div>
            </div>
            <UField label="Email" labelW={64} value={newAddr.email} onChange={(v) => setNA({ email: v })} />
            <div className="flex items-center gap-2 mb-1">
              <span className={LBL} style={{ width: 64, whiteSpace: 'nowrap' }}>GSTIN{REQ}</span>
              <Input size="small" className="flex-1 min-w-[120px]" status={gstinFormatError(newAddr.gstin) ? 'error' : undefined} placeholder="GSTIN / PAN / Aadhaar / URP"
                value={newAddr.gstin || ''} onChange={(e) => setNA({ gstin: e.target.value })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button size="small" onClick={() => { setAddMode(false); setNewAddr({}); }}>Cancel</Button>
              <Button size="small" type="primary" onClick={saveNewDelivery}>Save delivery address</Button>
            </div>
          </div>
        ) : (
          <Table size="small" rowKey="id" dataSource={filteredAddresses} columns={addrColumns}
            tableLayout="fixed"
            pagination={{ pageSize: 10, size: 'small', showSizeChanger: false,
              showTotal: (total) => `${total} address${total === 1 ? '' : 'es'}` }}
            onRow={(r) => ({ onClick: () => chooseDelivery(r.id), style: { cursor: 'pointer' } })}
            locale={{ emptyText: custAddresses.length ? 'No matching addresses.' : 'No saved delivery addresses — use “Add New Address”.' }} />
        )}
      </Modal>
    </>
  );
}
